import type { ReactNode } from 'react';
import { Alert, Card, CardContent, CardHeader, CardTitle, LoadingSpinner, Pagination, QueryErrorAlert } from '@shared/components';
import type { DealerFinancialsView } from '../../hooks/useDealerFinancials';
import type { DealerFundStatement } from '../../types/dealerFundStatement.types';
import { DealerFundStatementDataTable } from './DealerFundStatementDataTable';
import { DealerFundStatementTotals } from './DealerFundStatementTotals';

interface Props extends DealerFinancialsView {
    includesOwnedDealers: boolean;
    extraSections?: ReactNode;
}

const headingId = 'dealer-financials-heading';

export function DealerFinancials({ extraSections, ...props }: Props) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <span id={headingId}>Financials</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    <section aria-labelledby={headingId}>
                        <FinancialsBody {...props} />
                    </section>
                    {extraSections}
                </div>
            </CardContent>
        </Card>
    );
}

function FinancialsBody({ isLoading, isError, unverified, notEntitled, statement, dealerNames, agreementRefs, agreementRefsFailed, includesOwnedDealers, onPageChange, onRetry }: Props) {
    if (isLoading) return <LoadingSpinner message="Loading fund statement" />;
    if (isError) {
        return <QueryErrorAlert variant="error" message="The ledger is unavailable, so the fund statement could not be loaded." onRetry={onRetry} />;
    }
    if (unverified) {
        return (
            <QueryErrorAlert
                variant="warning"
                message="The ledger is updating this dealer's agreement list, so the statement could not be checked. Try again in a few minutes."
                onRetry={onRetry}
            />
        );
    }
    if (notEntitled) {
        return <Alert variant="info" role="note">The fund statement is available only for the dealers you belong to.</Alert>;
    }
    if (!statement) return null;

    return (
        <div className="space-y-4">
            {includesOwnedDealers && (
                <p className="text-sm text-mp-muted">
                    Includes every agreement sold by this dealer and by the dealers it owns.
                </p>
            )}
            <DealerFundStatementTotals
                total={statement.total}
                accountCount={statement.accountCount}
                currency={statement.currency}
                roundingMode={statement.roundingMode}
            />
            <StatementRows statement={statement} dealerNames={dealerNames} agreementRefs={agreementRefs} agreementRefsFailed={agreementRefsFailed} onPageChange={onPageChange} />
        </div>
    );
}

function StatementRows({ statement, dealerNames, agreementRefs, agreementRefsFailed, onPageChange }: {
    statement: DealerFundStatement;
    dealerNames: Map<string, string>;
    agreementRefs: Map<string, string>;
    agreementRefsFailed: boolean;
    onPageChange: (page: number) => void;
}) {
    if (statement.accountCount === 0) {
        return <p className="text-sm text-mp-muted">No agreements have a fund account yet.</p>;
    }
    const { page, perPage, total, lastPage } = statement.meta;
    const from = (page - 1) * perPage + 1;
    const to = Math.min(total, from + statement.agreements.length - 1);
    return (
        <div className="space-y-3">
            <DealerFundStatementDataTable rows={statement.agreements} currency={statement.currency} dealerNames={dealerNames} agreementRefs={agreementRefs} />
            <p role="status" className="text-sm text-mp-muted">
                {agreementRefsFailed && 'Agreement references could not be loaded, so agreements are shown by their short id.'}
            </p>
            <Pagination
                currentPage={page}
                lastPage={lastPage}
                total={total}
                perPage={perPage}
                from={from}
                to={to}
                onPageChange={onPageChange}
            />
        </div>
    );
}
